import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { 
  Mail, KeyRound, User, AtSign, CheckCircle2, 
  AlertCircle, ArrowRight, ArrowLeft, Eye, EyeOff, 
  RefreshCw, Sparkles, ShieldCheck 
} from 'lucide-react';
import { 
  unifiedInitAuth, verifyRegistrationOtp, loginUser, 
  registerWithOtp, checkUsernameAvailability, googleLoginUser, 
  getAuthProviders 
} from '../api/client';
import { useAuth } from '../context/AuthContext';
import { getTranslation as t, translateError as te, isRTL, getAuthNotice } from '../utils/translations';
import { GoogleOAuthProvider, GoogleLogin } from '@react-oauth/google';
import '../styles/unified-auth.css';

const AUTH_PENDING_KEY = 'luxvono_pending_otp_state';

const getInitialAuthState = () => {
  // 1. Check URL parameters (e.g. from 1-Click Verification Email link: ?email=...&otp=123456)
  try {
    const params = new URLSearchParams(window.location.search);
    const urlEmail = params.get('email');
    const urlOtp = params.get('otp');
    if (urlEmail && urlOtp && urlOtp.trim().length === 6) {
      return {
        step: 2,
        email: urlEmail.trim().toLowerCase(),
        accountExists: false,
        existingFirstName: '',
        maskedPhone: '',
        otpDigits: urlOtp.trim().split('').slice(0, 6),
        autoVerifyCode: urlOtp.trim(),
        resendTimer: 60
      };
    }
  } catch (e) {}

  // 2. Check sessionStorage so page refreshes do NOT kick user out of Step 2!
  try {
    const raw = sessionStorage.getItem(AUTH_PENDING_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      // Valid for 10 minutes (600,000ms)
      if (parsed && parsed.email && (Date.now() - parsed.timestamp < 10 * 60 * 1000)) {
        const remaining = parsed.resendUntil ? Math.max(0, Math.ceil((parsed.resendUntil - Date.now()) / 1000)) : 60;
        return {
          step: parsed.step || 2,
          email: parsed.email,
          accountExists: Boolean(parsed.accountExists),
          existingFirstName: parsed.firstName || '',
          maskedPhone: parsed.maskedPhone || '',
          otpDigits: ['', '', '', '', '', ''],
          autoVerifyCode: null,
          resendTimer: remaining
        };
      } else {
        sessionStorage.removeItem(AUTH_PENDING_KEY);
      }
    }
  } catch (e) {}

  return {
    step: 1,
    email: '',
    accountExists: false,
    existingFirstName: '',
    maskedPhone: '',
    otpDigits: ['', '', '', '', '', ''],
    autoVerifyCode: null,
    resendTimer: 60
  };
};

const UnifiedAuthCard = ({ defaultMode = 'unified' }) => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { login } = useAuth();
  const isArabic = isRTL();

  const initial = useRef(getInitialAuthState()).current;

  // Step 1: Email, Step 2: OTP, Step 3: Password / Profile Details
  const [step, setStep] = useState(initial.step);
  const [email, setEmail] = useState(initial.email);
  const [accountExists, setAccountExists] = useState(initial.accountExists);
  const [existingFirstName, setExistingFirstName] = useState(initial.existingFirstName);
  const [maskedPhone, setMaskedPhone] = useState(initial.maskedPhone);

  // Step 2: 6-digit OTP
  const [otpDigits, setOtpDigits] = useState(initial.otpDigits);
  const [isAutoVerifying, setIsAutoVerifying] = useState(false);
  const [otpError, setOtpError] = useState('');
  const [resendTimer, setResendTimer] = useState(initial.resendTimer);
  const [isResending, setIsResending] = useState(false);
  const otpInputRefs = useRef([]);

  // Step 3 (Existing user): Password
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  // Step 3 (New user): Registration details & smart username
  const [regFirstName, setRegFirstName] = useState('');
  const [regLastName, setRegLastName] = useState('');
  const [regUsername, setRegUsername] = useState('');
  const [usernameState, setUsernameState] = useState({
    status: 'idle', // 'idle' | 'checking' | 'available' | 'taken'
    message: '',
    suggestions: []
  });
  const usernameDebounceRef = useRef(null);

  // Global feedback
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const [googleClientId, setGoogleClientId] = useState('');

  // Load Google Client ID
  useEffect(() => {
    let mounted = true;
    getAuthProviders().then(providers => {
      if (mounted && providers && providers.google) {
        setGoogleClientId(providers.google);
      }
    });
    return () => { mounted = false; };
  }, []);

  // OTP resend countdown
  useEffect(() => {
    if (step !== 2) return;
    const interval = setInterval(() => {
      setResendTimer(prev => (prev > 0 ? prev - 1 : 0));
    }, 1000);
    return () => clearInterval(interval);
  }, [step]);

  // Auto-verify and auto-copy OTP if loaded from 1-Click Verification link
  useEffect(() => {
    if (initial.autoVerifyCode && initial.email) {
      if (navigator.clipboard && typeof navigator.clipboard.writeText === 'function') {
        navigator.clipboard.writeText(initial.autoVerifyCode).catch(() => {});
      }
      executeAutoVerifyOtp(initial.autoVerifyCode, initial.email);
    }
  }, []);

  // Focus first OTP input when step 2 opens
  useEffect(() => {
    if (step === 2 && otpInputRefs.current[0]) {
      setTimeout(() => otpInputRefs.current[0]?.focus(), 150);
    }
  }, [step]);

  // Live Username Availability & Suggestions check (Debounced 350ms)
  useEffect(() => {
    if (step !== 3 || accountExists) return;
    const trimmed = regUsername.trim();
    if (!trimmed) {
      setUsernameState({ status: 'idle', message: '', suggestions: [] });
      return;
    }
    if (trimmed.length < 3) {
      setUsernameState({ 
        status: 'taken', 
        message: getAuthNotice('username_min_length'),
        suggestions: []
      });
      return;
    }

    setUsernameState(prev => ({ ...prev, status: 'checking' }));
    if (usernameDebounceRef.current) clearTimeout(usernameDebounceRef.current);

    usernameDebounceRef.current = setTimeout(async () => {
      try {
        const res = await checkUsernameAvailability(trimmed);
        if (res.available) {
          setUsernameState({
            status: 'available',
            message: res.message || (isArabic ? 'اسم المستخدم متاح' : 'Username is available'),
            suggestions: []
          });
        } else {
          setUsernameState({
            status: 'taken',
            message: res.message || (isArabic ? 'هذا الاسم مستخدم بالفعل' : 'Username is already taken'),
            suggestions: res.suggestions || []
          });
        }
      } catch (err) {
        console.error('Username check error:', err);
      }
    }, 350);

    return () => {
      if (usernameDebounceRef.current) clearTimeout(usernameDebounceRef.current);
    };
  }, [regUsername, step, accountExists, isArabic]);

  // Handle Google Login
  const handleGoogleSuccess = async (credentialResponse) => {
    try {
      setLoading(true);
      setError('');
      const payload = await googleLoginUser(credentialResponse.credential);
      if (payload && payload.accessToken) {
        try {
          sessionStorage.removeItem(AUTH_PENDING_KEY);
        } catch (e) {}
        if (window.AndroidBridge && typeof window.AndroidBridge.onAuthSuccess === 'function') {
          window.AndroidBridge.onAuthSuccess(payload.accessToken);
        }
        const redirectParam = searchParams.get('redirect') || '/';
        login(payload);
        navigate(redirectParam);
      } else {
        setError(te('Invalid response from server.'));
      }
    } catch (err) {
      console.error('Google Auth Error:', err);
      setError(te(err));
    } finally {
      setLoading(false);
    }
  };

  // Step 1: Submit Email
  const handleEmailSubmit = async (e) => {
    if (e) e.preventDefault();
    const cleanEmail = email.trim().toLowerCase();
    if (!cleanEmail || !cleanEmail.includes('@')) {
      setError(te('Please enter a valid email address.'));
      return;
    }

    setLoading(true);
    setError('');
    setSuccessMsg('');

    try {
      const res = await unifiedInitAuth({ email: cleanEmail });
      setAccountExists(Boolean(res.accountExists));
      setExistingFirstName(res.firstName || '');
      setMaskedPhone(res.maskedPhone || '');
      setOtpDigits(['', '', '', '', '', '']);
      setOtpError('');
      setResendTimer(60);
      setStep(2);
      setSuccessMsg(getAuthNotice('otp_sent'));

      try {
        sessionStorage.setItem(AUTH_PENDING_KEY, JSON.stringify({
          step: 2,
          email: cleanEmail,
          accountExists: Boolean(res.accountExists),
          firstName: res.firstName || '',
          maskedPhone: res.maskedPhone || '',
          timestamp: Date.now(),
          resendUntil: Date.now() + 60000
        }));
      } catch (e) {}
    } catch (err) {
      console.error('Unified Auth Init Error:', err);
      setError(te(err));
    } finally {
      setLoading(false);
    }
  };

  // Step 2: Auto Verify OTP (Zero-Click Auto Recognition)
  const executeAutoVerifyOtp = async (code, targetEmail) => {
    if (!code || code.length !== 6) return;
    const emailToUse = (targetEmail || email || '').trim().toLowerCase();
    if (!emailToUse) return;

    setIsAutoVerifying(true);
    setOtpError('');
    setError('');

    try {
      await verifyRegistrationOtp({ email: emailToUse, otp: code });
      setIsAutoVerifying(false);
      setSuccessMsg(getAuthNotice('otp_confirmed'));
      // Transition smoothly to Step 3
      setStep(3);
    } catch (err) {
      setIsAutoVerifying(false);
      setOtpError(te(err));
    }
  };

  // Handle Back to Step 1 & Clear Pending State
  const handleBackToStep1 = () => {
    try {
      sessionStorage.removeItem(AUTH_PENDING_KEY);
    } catch (e) {}
    setStep(1);
    setError('');
    setOtpError('');
  };

  // Handle Direct Paste from Clipboard via button
  const handlePasteFromClipboard = async () => {
    try {
      if (!navigator.clipboard || typeof navigator.clipboard.readText !== 'function') {
        return;
      }
      const clipText = await navigator.clipboard.readText();
      const digitsOnly = (clipText || '').replace(/\D/g, '').slice(0, 6);
      if (digitsOnly.length === 6) {
        setOtpDigits(digitsOnly.split(''));
        executeAutoVerifyOtp(digitsOnly);
      }
    } catch (e) {
      console.warn('Clipboard read error:', e);
    }
  };

  // Handle OTP digit changes
  const handleOtpDigitChange = (index, value) => {
    const rawVal = value.replace(/\D/g, '');
    const newDigits = [...otpDigits];

    if (!rawVal) {
      newDigits[index] = '';
      setOtpDigits(newDigits);
      return;
    }

    if (rawVal.length > 1) {
      // User pasted or typed multiple digits in a single cell
      const pastedDigits = rawVal.slice(0, 6).split('');
      for (let i = 0; i < 6; i++) {
        newDigits[i] = pastedDigits[i] || '';
      }
      setOtpDigits(newDigits);
      const lastIndex = Math.min(pastedDigits.length, 5);
      otpInputRefs.current[lastIndex]?.focus();

      if (newDigits.every(d => d !== '')) {
        executeAutoVerifyOtp(newDigits.join(''));
      }
      return;
    }

    // Single digit input
    newDigits[index] = rawVal;
    setOtpDigits(newDigits);

    // Auto-advance focus to next cell
    if (index < 5) {
      otpInputRefs.current[index + 1]?.focus();
    }

    // Check if all 6 cells are filled -> Auto-verify instantly!
    if (newDigits.every(d => d !== '')) {
      executeAutoVerifyOtp(newDigits.join(''));
    }
  };

  // Handle Backspace navigation in OTP cells
  const handleOtpKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otpDigits[index] && index > 0) {
      otpInputRefs.current[index - 1]?.focus();
    }
  };

  // Handle Clipboard Paste on ANY of the 6 OTP cells
  const handleOtpPaste = (e) => {
    e.preventDefault();
    const pastedText = e.clipboardData.getData('text');
    const digitsOnly = pastedText.replace(/\D/g, '').slice(0, 6);
    if (!digitsOnly) return;

    const newDigits = ['', '', '', '', '', ''];
    for (let i = 0; i < digitsOnly.length; i++) {
      newDigits[i] = digitsOnly[i];
    }
    setOtpDigits(newDigits);

    const focusTarget = Math.min(digitsOnly.length, 5);
    otpInputRefs.current[focusTarget]?.focus();

    // If pasted full 6 digits, auto-recognize and verify immediately!
    if (digitsOnly.length === 6) {
      executeAutoVerifyOtp(digitsOnly);
    }
  };

  // Resend OTP
  const handleResendOtp = async () => {
    if (resendTimer > 0 || isResending) return;
    setIsResending(true);
    setOtpError('');
    try {
      await unifiedInitAuth({ email: email.trim().toLowerCase() });
      setResendTimer(60);
      try {
        const raw = sessionStorage.getItem(AUTH_PENDING_KEY);
        if (raw) {
          const parsed = JSON.parse(raw);
          parsed.resendUntil = Date.now() + 60000;
          sessionStorage.setItem(AUTH_PENDING_KEY, JSON.stringify(parsed));
        }
      } catch (e) {}
      setSuccessMsg(getAuthNotice('otp_sent'));
    } catch (err) {
      setOtpError(te(err));
    } finally {
      setIsResending(false);
    }
  };

  // Step 3A: Existing User Login with Password
  const handleExistingUserLogin = async (e) => {
    if (e) e.preventDefault();
    if (!password) {
      setError(te('Please enter your password.'));
      return;
    }

    setLoading(true);
    setError('');

    try {
      const payload = await loginUser({ email: email.trim().toLowerCase(), password });
      if (payload && payload.accessToken) {
        try {
          sessionStorage.removeItem(AUTH_PENDING_KEY);
        } catch (e) {}
        login(payload);
        const redirectParam = searchParams.get('redirect') || '/';
        navigate(redirectParam);
      } else {
        setError(te('Invalid response from server.'));
      }
    } catch (err) {
      console.error('Login error:', err);
      setError(te(err));
    } finally {
      setLoading(false);
    }
  };

  // Step 3B: New User Registration with Details & Username
  const handleNewUserRegister = async (e) => {
    if (e) e.preventDefault();
    if (!regFirstName.trim() || !regLastName.trim()) {
      setError(te('Please enter your first and last name.'));
      return;
    }
    if (!password || password.length < 8) {
      setError(te('Password must be at least 8 characters.'));
      return;
    }
    if (usernameState.status === 'taken') {
      setError(te('Please choose an available username or click a suggestion.'));
      return;
    }

    setLoading(true);
    setError('');

    try {
      const payload = await registerWithOtp({
        email: email.trim().toLowerCase(),
        otp: otpDigits.join(''),
        firstName: regFirstName.trim(),
        lastName: regLastName.trim(),
        username: regUsername.trim() || undefined,
        password: password
      });

      if (payload && payload.accessToken) {
        try {
          sessionStorage.removeItem(AUTH_PENDING_KEY);
        } catch (e) {}
        login(payload);
        const redirectParam = searchParams.get('redirect') || '/';
        navigate(redirectParam);
      } else {
        setError(te('Invalid response from server.'));
      }
    } catch (err) {
      console.error('Registration error:', err);
      setError(te(err));
    } finally {
      setLoading(false);
    }
  };

  // Apply smart username suggestion
  const applyUsernameSuggestion = (suggested) => {
    setRegUsername(suggested);
    setUsernameState({
      status: 'available',
      message: isArabic ? 'اسم المستخدم متاح' : 'Username is available',
      suggestions: []
    });
  };

  return (
    <div className="unified-auth-page" dir={isArabic ? 'rtl' : 'ltr'}>
      <div className="unified-auth-card">
        
        {/* Brand Sovereign Header */}
        <div className="auth-brand-header">
          <div className="auth-crest-wrap">
            <ShieldCheck size={32} strokeWidth={2.2} />
          </div>
          <div>
            <span className="auth-brand-tag">LUXVONO PLATFORM</span>
          </div>
          <h1 className="auth-title">
            {step === 1 && (isArabic ? 'تسجيل الدخول أو الانضمام' : 'Sign In or Join Luxvono')}
            {step === 2 && (isArabic ? 'رمز التحقق والأمان' : 'Security Verification Code')}
            {step === 3 && accountExists && (
              existingFirstName 
                ? (isArabic ? `مرحباً بعودتك يا ${existingFirstName}!` : `Welcome Back, ${existingFirstName}!`) 
                : (isArabic ? 'مرحباً بعودتك مجدداً!' : 'Welcome Back!')
            )}
            {step === 3 && !accountExists && (isArabic ? 'إكمال حسابك الملكي' : 'Complete Your Luxvono Account')}
          </h1>
          <p className="auth-subtitle">
            {step === 1 && (isArabic 
              ? 'أدخل بريدك الإلكتروني للمتابعة فوراً مع أعلى معايير الخصوصية والأمان.' 
              : 'Enter your email to proceed securely to your sovereign account.')}
            {step === 2 && (isArabic 
              ? 'أدخل الرمز المكون من 6 أرقام للتعرف الفوري والتحقق التلقائي.' 
              : 'Enter the 6-digit code for instant recognition and automatic login.')}
            {step === 3 && accountExists && (isArabic 
              ? 'تم التحقق من هويتك بنجاح. يرجى إدخال كلمة المرور للمتابعة.' 
              : 'Your identity has been verified. Enter your password to continue.')}
            {step === 3 && !accountExists && (isArabic 
              ? 'خطوة أخيرة لإطلاق تجربة التسوق الفاخرة والاستفادة من المزايا الحصرية.' 
              : 'Final step to access bespoke luxury services and member privileges.')}
          </p>
        </div>

        {/* Stepper Progress Indicator */}
        <div className="auth-steps-progress">
          <div className={`auth-step-dot ${step === 1 ? 'active' : step > 1 ? 'completed' : ''}`}>
            <span className="auth-step-num">{step > 1 ? '✓' : '1'}</span>
            <span>{isArabic ? 'البريد' : 'Email'}</span>
          </div>
          <div className={`auth-step-line ${step >= 2 ? 'active' : ''}`} />
          <div className={`auth-step-dot ${step === 2 ? 'active' : step > 2 ? 'completed' : ''}`}>
            <span className="auth-step-num">{step > 2 ? '✓' : '2'}</span>
            <span>{isArabic ? 'التحقق' : 'Verify'}</span>
          </div>
          <div className={`auth-step-line ${step >= 3 ? 'active' : ''}`} />
          <div className={`auth-step-dot ${step === 3 ? 'active' : ''}`}>
            <span className="auth-step-num">3</span>
            <span>{accountExists ? (isArabic ? 'الدخول' : 'Access') : (isArabic ? 'الحساب' : 'Account')}</span>
          </div>
        </div>

        {/* Alerts / Error feedback */}
        {error && (
          <div className="auth-msg-banner auth-msg-error">
            <AlertCircle size={18} style={{ flexShrink: 0, marginTop: 1 }} />
            <div>{error}</div>
          </div>
        )}

        {successMsg && !error && (
          <div className="auth-msg-banner auth-msg-success">
            <CheckCircle2 size={18} style={{ flexShrink: 0, marginTop: 1 }} />
            <div>{successMsg}</div>
          </div>
        )}

        {/* ══════════════════════════════════════════════════════════════
            STEP 1: UNIFIED EMAIL ENTRY
            ══════════════════════════════════════════════════════════════ */}
        {step === 1 && (
          <form onSubmit={handleEmailSubmit}>
            <div className="auth-input-group">
              <label className="auth-label">{isArabic ? 'البريد الإلكتروني' : 'Email Address'}</label>
              <div className="auth-input-wrap">
                <input
                  type="email"
                  className="auth-text-input"
                  placeholder="name@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  autoFocus
                  required
                />
                <Mail size={18} className="auth-input-icon" />
              </div>
            </div>

            <button 
              type="submit" 
              className="auth-btn-primary" 
              disabled={loading || !email.trim()}
            >
              {loading ? (
                <>
                  <RefreshCw size={18} className="spin-animation" />
                  <span>{isArabic ? 'جاري التحقق...' : 'Checking...'}</span>
                </>
              ) : (
                <>
                  <span>{isArabic ? 'متابعة' : 'Continue'}</span>
                  {isArabic ? <ArrowLeft size={18} /> : <ArrowRight size={18} />}
                </>
              )}
            </button>

            {/* Google Social Login */}
            {googleClientId && (
              <>
                <div className="auth-social-divider">
                  <span>{isArabic ? 'أو المتابعة السريعة عبر' : 'Or quick sign in with'}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'center' }}>
                  <GoogleOAuthProvider clientId={googleClientId}>
                    <GoogleLogin
                      onSuccess={handleGoogleSuccess}
                      onError={() => setError(te('Google Login cancelled.'))}
                      theme="filled_black"
                      shape="pill"
                      size="large"
                      text="continue_with"
                      locale={isArabic ? 'ar' : 'en'}
                    />
                  </GoogleOAuthProvider>
                </div>
              </>
            )}
          </form>
        )}

        {/* ══════════════════════════════════════════════════════════════
            STEP 2: 6-DIGIT OTP WITH ZERO-CLICK AUTO-VERIFICATION
            ══════════════════════════════════════════════════════════════ */}
        {step === 2 && (
          <div>
            {/* Target Email Banner with Change button */}
            <div className="auth-target-email">
              <span className="target-email-text">{email}</span>
              <button 
                type="button" 
                onClick={handleBackToStep1} 
                className="target-email-change"
              >
                {isArabic ? 'تعديل البريد' : 'Change Email'}
              </button>
            </div>

            {/* 6 Individual Digit Cells with Paste & Auto-Recognition */}
            <div className="otp-cells-container" onPaste={handleOtpPaste}>
              {otpDigits.map((digit, index) => (
                <input
                  key={index}
                  ref={(el) => (otpInputRefs.current[index] = el)}
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  className={`otp-digit-cell ${digit ? 'filled' : ''}`}
                  value={digit}
                  onChange={(e) => handleOtpDigitChange(index, e.target.value)}
                  onKeyDown={(e) => handleOtpKeyDown(index, e)}
                  disabled={isAutoVerifying}
                />
              ))}
            </div>

            {/* Quick Paste from Clipboard Action */}
            <div style={{ display: 'flex', justifyContent: 'center', marginTop: 12, marginBottom: 14 }}>
              <button
                type="button"
                onClick={handlePasteFromClipboard}
                style={{
                  background: 'rgba(255, 255, 255, 0.05)',
                  border: '1px solid rgba(255, 255, 255, 0.12)',
                  color: '#E5E7EB',
                  borderRadius: '10px',
                  padding: '7px 16px',
                  fontSize: '12.5px',
                  cursor: 'pointer',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '6px',
                  transition: 'all 0.2s',
                  fontWeight: 600
                }}
              >
                📋 {isArabic ? 'لصق الرمز وتأكيده تلقائياً' : 'Paste Code & Auto-Verify'}
              </button>
            </div>

            {/* Auto-verification loading indicator */}
            {isAutoVerifying && (
              <div className="otp-auto-verifying">
                <RefreshCw size={16} className="spin-animation" />
                <span>{isArabic ? 'جاري التعرف التلقائي والتحقق من الرمز...' : 'Auto-verifying code...'}</span>
              </div>
            )}

            {/* OTP specific error */}
            {otpError && (
              <div className="auth-msg-banner auth-msg-error" style={{ marginBottom: 14 }}>
                <AlertCircle size={16} style={{ flexShrink: 0 }} />
                <div>{otpError}</div>
              </div>
            )}

            {/* Manual Verification Button if needed */}
            <button
              type="button"
              className="auth-btn-primary"
              onClick={() => executeAutoVerifyOtp(otpDigits.join(''))}
              disabled={isAutoVerifying || otpDigits.some(d => d === '')}
            >
              <ShieldCheck size={18} />
              <span>{isArabic ? 'تأكيد الرمز والمتابعة' : 'Confirm Code & Continue'}</span>
            </button>

            {/* Resend OTP button & timer */}
            <div style={{ textAlign: 'center', marginTop: 18 }}>
              {resendTimer > 0 ? (
                <span style={{ fontSize: 12.5, color: '#9CA3AF' }}>
                  {getAuthNotice('resend_otp_countdown')} {resendTimer}s
                </span>
              ) : (
                <button
                  type="button"
                  onClick={handleResendOtp}
                  disabled={isResending}
                  className="target-email-change"
                  style={{ fontSize: 13 }}
                >
                  {isResending ? getAuthNotice('resend_otp_sending') : getAuthNotice('resend_otp_btn')}
                </button>
              )}
            </div>
          </div>
        )}

        {/* ══════════════════════════════════════════════════════════════
            STEP 3A: EXISTING USER PASSWORD ENTRY
            ══════════════════════════════════════════════════════════════ */}
        {step === 3 && accountExists && (
          <form onSubmit={handleExistingUserLogin}>
            <div className="auth-target-email">
              <span className="target-email-text">{email}</span>
              <span style={{ fontSize: 12, color: '#10B981', fontWeight: 700 }}>
                ✓ {isArabic ? 'تم تأكيد الهوية' : 'Identity Verified'}
              </span>
            </div>

            <div className="auth-input-group">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                <label className="auth-label" style={{ margin: 0 }}>
                  {isArabic ? 'كلمة المرور' : 'Password'}
                </label>
                <Link 
                  to="/forgot-password" 
                  style={{ fontSize: 12, color: '#FE2C55', textDecoration: 'none', fontWeight: 600 }}
                >
                  {isArabic ? 'نسيت كلمة المرور؟' : 'Forgot Password?'}
                </Link>
              </div>
              <div className="auth-input-wrap">
                <input
                  type={showPassword ? 'text' : 'password'}
                  className="auth-text-input"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoFocus
                  required
                />
                <KeyRound size={18} className="auth-input-icon" />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="auth-pwd-toggle"
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            <button 
              type="submit" 
              className="auth-btn-primary" 
              disabled={loading || !password}
            >
              {loading ? (
                <>
                  <RefreshCw size={18} className="spin-animation" />
                  <span>{isArabic ? 'جاري تسجيل الدخول...' : 'Signing in...'}</span>
                </>
              ) : (
                <>
                  <span>{isArabic ? 'تسجيل الدخول' : 'Sign In'}</span>
                  {isArabic ? <ArrowLeft size={18} /> : <ArrowRight size={18} />}
                </>
              )}
            </button>
          </form>
        )}

        {/* ══════════════════════════════════════════════════════════════
            STEP 3B: NEW USER DETAILS & SMART USERNAME AVAILABILITY
            ══════════════════════════════════════════════════════════════ */}
        {step === 3 && !accountExists && (
          <form onSubmit={handleNewUserRegister}>
            <div className="auth-target-email">
              <span className="target-email-text">{email}</span>
              <span style={{ fontSize: 12, color: '#10B981', fontWeight: 700 }}>
                ✓ {isArabic ? 'بريد موثق' : 'Verified Email'}
              </span>
            </div>

            {/* Name Grid */}
            <div className="auth-grid-2">
              <div className="auth-input-group">
                <label className="auth-label">{isArabic ? 'الاسم الأول' : 'First Name'}</label>
                <div className="auth-input-wrap">
                  <input
                    type="text"
                    className="auth-text-input"
                    placeholder={isArabic ? 'محمد' : 'John'}
                    value={regFirstName}
                    onChange={(e) => setRegFirstName(e.target.value)}
                    required
                    autoFocus
                  />
                  <User size={18} className="auth-input-icon" />
                </div>
              </div>

              <div className="auth-input-group">
                <label className="auth-label">{isArabic ? 'اسم العائلة' : 'Last Name'}</label>
                <div className="auth-input-wrap">
                  <input
                    type="text"
                    className="auth-text-input"
                    placeholder={isArabic ? 'العتيبي' : 'Doe'}
                    value={regLastName}
                    onChange={(e) => setRegLastName(e.target.value)}
                    required
                  />
                  <User size={18} className="auth-input-icon" />
                </div>
              </div>
            </div>

            {/* Username with Live Check & Clickable Suggestions */}
            <div className="auth-input-group">
              <label className="auth-label">
                {isArabic ? 'اسم المستخدم (Username)' : 'Username'}
              </label>
              <div className="auth-input-wrap">
                <input
                  type="text"
                  className={`auth-text-input ${
                    usernameState.status === 'available' ? 'input-valid' : 
                    usernameState.status === 'taken' ? 'input-error' : ''
                  }`}
                  placeholder={isArabic ? 'ahmed_lux' : 'john_lux'}
                  value={regUsername}
                  onChange={(e) => setRegUsername(e.target.value)}
                  style={{ direction: 'ltr', textAlign: isArabic ? 'right' : 'left' }}
                />
                <AtSign size={18} className="auth-input-icon" />
              </div>

              {/* Username feedback status */}
              {usernameState.status === 'checking' && (
                <div className="username-status-box" style={{ color: '#FDA4AF' }}>
                  <RefreshCw size={12} className="spin-animation" style={{ display: 'inline', marginLeft: 4, marginRight: 4 }} />
                  {isArabic ? 'جاري فحص توافر اسم المستخدم...' : 'Checking availability...'}
                </div>
              )}

              {usernameState.status === 'available' && (
                <div className="username-status-box">
                  <span className="username-status-available">
                    <CheckCircle2 size={14} />
                    {usernameState.message}
                  </span>
                </div>
              )}

              {usernameState.status === 'taken' && (
                <div className="username-status-box">
                  <span className="username-status-taken">
                    <AlertCircle size={14} />
                    {usernameState.message}
                  </span>

                  {/* Smart Clickable Suggestions */}
                  {usernameState.suggestions && usernameState.suggestions.length > 0 && (
                    <div className="username-suggestions-wrap">
                      <span className="suggestions-title">
                        {isArabic ? 'اقتراحات أسماء مستخدمين بديلة متاحة:' : 'Suggested available usernames:'}
                      </span>
                      {usernameState.suggestions.map((suggestion) => (
                        <button
                          key={suggestion}
                          type="button"
                          onClick={() => applyUsernameSuggestion(suggestion)}
                          className="username-chip"
                          title={isArabic ? 'انقر للاختيار الفوري' : 'Click to select'}
                        >
                          @{suggestion}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Password */}
            <div className="auth-input-group">
              <label className="auth-label">{isArabic ? 'كلمة المرور' : 'Password'}</label>
              <div className="auth-input-wrap">
                <input
                  type={showPassword ? 'text' : 'password'}
                  className="auth-text-input"
                  placeholder={isArabic ? '8 أحرف على الأقل' : 'At least 8 characters'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
                <KeyRound size={18} className="auth-input-icon" />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="auth-pwd-toggle"
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            <button 
              type="submit" 
              className="auth-btn-primary" 
              disabled={loading || !regFirstName || !regLastName || !password || usernameState.status === 'taken'}
            >
              {loading ? (
                <>
                  <RefreshCw size={18} className="spin-animation" />
                  <span>{isArabic ? 'جاري إنشاء الحساب...' : 'Creating account...'}</span>
                </>
              ) : (
                <>
                  <Sparkles size={18} />
                  <span>{isArabic ? 'إتمام التسجيل والدخول' : 'Complete Setup & Sign In'}</span>
                </>
              )}
            </button>
          </form>
        )}

      </div>
    </div>
  );
};

export default UnifiedAuthCard;
